// Physical Inventory Count — packaging-side mirror of IngredientPhysicalCountModal.js (see
// docs/PHYSICAL_INVENTORY_COUNT_MODULE.md). Packaging materials have no lot-code concept, so this
// is simpler: one editable row per material.
import React, { useState, useEffect, useMemo } from 'react';
import packagingService from '../services/packagingService';

const today = () => new Date().toISOString().split('T')[0];

const REASON_LABELS = { count: 'Stock Count', waste: 'Waste / Write-Off', correction: 'Correction', other: 'Other' };

export default function PackagingPhysicalCountModal({ onClose, onCompleted }) {
  const [phase, setPhase] = useState('loading'); // loading | review | submitting | done | error
  const [error, setError] = useState('');
  const [materials, setMaterials] = useState([]);
  const [rows, setRows] = useState({}); // materialId -> { countedQty, reason, notes }
  const [search, setSearch] = useState('');
  const [countedAt, setCountedAt] = useState(today());
  const [sessionNotes, setSessionNotes] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    packagingService.getPhysicalCountDraftSheet()
      .then(sheet => {
        setMaterials(sheet);
        setRows(Object.fromEntries(sheet.map(m => [m.packagingMaterialId, { countedQty: '', reason: 'count', notes: '' }])));
        setPhase('review');
      })
      .catch(e => {
        setError(e?.response?.data?.message || e.message || 'Failed to load packaging materials.');
        setPhase('error');
      });
  }, []);

  const setRow = (id, field, value) => {
    setRows(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const filteredMaterials = useMemo(() => {
    if (!search.trim()) return materials;
    const q = search.trim().toLowerCase();
    return materials.filter(m => m.name.toLowerCase().includes(q));
  }, [materials, search]);

  const deltaFor = (material) => {
    const row = rows[material.packagingMaterialId];
    if (!row || row.countedQty === '') return null;
    const counted = parseFloat(row.countedQty);
    if (isNaN(counted)) return null;
    return Math.round((counted - material.systemBalance) * 100) / 100;
  };

  const countedRowCount = Object.values(rows).filter(r => r.countedQty !== '').length;

  const handleSubmit = async () => {
    const lines = materials
      .filter(m => rows[m.packagingMaterialId]?.countedQty !== '')
      .map(m => {
        const row = rows[m.packagingMaterialId];
        return {
          packagingMaterialId: m.packagingMaterialId,
          countedQty: parseFloat(row.countedQty),
          reason: row.reason,
          notes: row.notes || null,
        };
      });
    if (lines.length === 0) {
      setError('Enter at least one counted quantity.');
      return;
    }
    setError('');
    setPhase('submitting');
    try {
      const res = await packagingService.submitPhysicalCount({ countedAt, notes: sessionNotes || null, lines });
      setResult(res);
      setPhase('done');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to submit the count.');
      setPhase('review');
    }
  };

  const handleClose = () => {
    if (phase === 'done') onCompleted();
    onClose();
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">📋 Physical Inventory Count</h5>
            <button type="button" className="btn-close" onClick={handleClose} disabled={phase === 'submitting'} />
          </div>
          <div className="modal-body">
            {phase === 'loading' && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" />
              </div>
            )}

            {phase === 'error' && <div className="alert alert-danger">{error}</div>}

            {(phase === 'review' || phase === 'submitting') && (
              <>
                {error && <div className="alert alert-danger py-2">{error}</div>}
                <div className="row g-3 mb-3">
                  <div className="col-md-3">
                    <label className="form-label">Count Date</label>
                    <input type="date" className="form-control" value={countedAt} onChange={e => setCountedAt(e.target.value)} />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label">Notes (optional)</label>
                    <input type="text" className="form-control" value={sessionNotes}
                      onChange={e => setSessionNotes(e.target.value)} placeholder="e.g. Monthly count — October" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Filter</label>
                    <input type="text" className="form-control" value={search}
                      onChange={e => setSearch(e.target.value)} placeholder="Search material…" />
                  </div>
                </div>

                <div className="text-muted small mb-2">
                  Enter the counted quantity only for items you're checking this time.
                  {countedRowCount > 0 && <strong> {countedRowCount} counted so far.</strong>}
                </div>

                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Material</th>
                      <th style={{ width: 110 }}>System</th>
                      <th style={{ width: 120 }}>Counted</th>
                      <th style={{ width: 90 }}>Delta</th>
                      <th style={{ width: 150 }}>Reason</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMaterials.map(m => {
                      const row = rows[m.packagingMaterialId] || { countedQty: '', reason: 'count', notes: '' };
                      const delta = deltaFor(m);
                      return (
                        <tr key={m.packagingMaterialId} className={delta != null && Math.abs(delta) >= 0.01 ? 'table-warning' : ''}>
                          <td>{m.name} <span className="text-muted" style={{ fontSize: 11 }}>({m.unitOfMeasure})</span></td>
                          <td>{Number(m.systemBalance).toFixed(2)}</td>
                          <td>
                            <input type="number" className="form-control form-control-sm" value={row.countedQty}
                              onChange={e => setRow(m.packagingMaterialId, 'countedQty', e.target.value)} />
                          </td>
                          <td className={delta == null ? '' : delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-muted'}>
                            {delta == null ? '—' : `${delta > 0 ? '+' : ''}${delta.toFixed(2)}`}
                          </td>
                          <td>
                            {delta != null && Math.abs(delta) >= 0.01 ? (
                              <select className="form-select form-select-sm" value={row.reason}
                                onChange={e => setRow(m.packagingMaterialId, 'reason', e.target.value)}>
                                {Object.entries(REASON_LABELS).map(([val, label]) => (
                                  <option key={val} value={val}>{label}</option>
                                ))}
                              </select>
                            ) : <span className="text-muted">—</span>}
                          </td>
                          <td>
                            <input type="text" className="form-control form-control-sm" value={row.notes}
                              onChange={e => setRow(m.packagingMaterialId, 'notes', e.target.value)} placeholder="optional" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}

            {phase === 'done' && (
              <div className="py-3">
                <div className="text-center mb-3">
                  <div className="mb-2" style={{ fontSize: 32 }}>✅</div>
                  <div className="fw-semibold">
                    Counted {result?.lineCount || 0} item{result?.lineCount === 1 ? '' : 's'} —
                    {' '}{result?.adjustmentCount || 0} adjustment{result?.adjustmentCount === 1 ? '' : 's'} created.
                  </div>
                </div>
                {(result?.lines || []).filter(l => l.adjustmentId).length > 0 && (
                  <table className="table table-sm">
                    <thead><tr><th>Material</th><th>Delta</th></tr></thead>
                    <tbody>
                      {result.lines.filter(l => l.adjustmentId).map(l => {
                        const m = materials.find(x => x.packagingMaterialId === l.packagingMaterialId);
                        return (
                          <tr key={l.id}>
                            <td>{m?.name || l.packagingMaterialId}</td>
                            <td className={Number(l.delta) >= 0 ? 'text-success' : 'text-danger'}>
                              {Number(l.delta) > 0 ? '+' : ''}{Number(l.delta).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            {phase !== 'done' && (
              <button className="btn btn-outline-secondary" onClick={handleClose} disabled={phase === 'submitting'}>Cancel</button>
            )}
            {(phase === 'review' || phase === 'submitting') && (
              <button className="btn btn-primary" onClick={handleSubmit} disabled={phase === 'submitting' || countedRowCount === 0}>
                {phase === 'submitting' ? <><span className="spinner-border spinner-border-sm me-2" />Submitting…</> : `Complete Count (${countedRowCount})`}
              </button>
            )}
            {phase === 'done' && (
              <button className="btn btn-primary" onClick={handleClose}>Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
